import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons/faMagnifyingGlass';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Autocomplete, Loader } from '@mantine/core';
import axios from 'axios';

import { getFreightFulfillmentPlanCommodityPresets } from '../../../../api/ltl.api';

import type { FreightFulfillmentPlanCommodityPreset } from '../../../../types/freight-responses';

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 500;

// Separator that won't appear in a saved commodity preset's name.
const ITEM_VALUE_SEPARATOR = '|||';

/**
 * Encodes the display name and commodityPresetId into a single Mantine option
 * value. The id suffix guarantees uniqueness even when two saved presets share
 * the same name.
 */
const buildItemValue = (commodityPreset: FreightFulfillmentPlanCommodityPreset): string =>
  `${commodityPreset.name}${ITEM_VALUE_SEPARATOR}${commodityPreset.commodityPresetId}`;

const extractLabel = (itemValue: string): string => itemValue.split(ITEM_VALUE_SEPARATOR)[0];

const extractCommodityPresetId = (itemValue: string): string => itemValue.split(ITEM_VALUE_SEPARATOR)[1] ?? '';

export type CommodityProductAutocompleteProps = {
  value: string;
  onChange: (value: string) => void;
  /** Called with the full record when a saved commodity preset is picked. */
  onCommodityPresetSelect: (commodityPreset: FreightFulfillmentPlanCommodityPreset) => void;
  label: string;
  placeholder: string;
  'data-testid'?: string;
};

/**
 * A self-fetching Product Name search field built on Mantine's `Autocomplete`,
 * searching seller-saved commodity presets from `/freight/commodity-preset`.
 *
 * As the user types it fetches suggestions once at least {@link MIN_SEARCH_LENGTH}
 * characters are entered, debounced by {@link SEARCH_DEBOUNCE_MS}. A change while a
 * request is in flight aborts the prior request and issues a new one, so only the
 * latest query's results land.
 */
export const CommodityProductAutocomplete = ({
  value,
  onChange,
  onCommodityPresetSelect,
  label,
  placeholder,
  'data-testid': dataTestId,
}: CommodityProductAutocompleteProps) => {
  const [commodityPresets, setCommodityPresets] = useState<FreightFulfillmentPlanCommodityPreset[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Mantine fires onChange with the picked option's raw value immediately after
  // onOptionSubmit. That trailing change would clobber the just-applied autofill
  // with the encoded "name|||id" string, so we swallow exactly one onChange after a submit.
  const didJustSubmitRef = useRef(false);

  // Abort any in-flight request and cancel a pending debounce on unmount.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      abortControllerRef.current?.abort();
    };
  }, []);

  // Key by commodityPresetId so two presets with identical names resolve correctly.
  const commodityPresetById = useMemo(
    () => new Map(commodityPresets.map((commodityPreset) => [String(commodityPreset.commodityPresetId), commodityPreset])),
    [commodityPresets],
  );
  const data = useMemo(() => commodityPresets.map(buildItemValue), [commodityPresets]);

  const runSearch = useCallback((searchString: string) => {
    // Cancel the request in flight; the latest query wins.
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsSearching(true);
    getFreightFulfillmentPlanCommodityPresets(searchString, controller.signal)
      .then((results) => {
        if (!controller.signal.aborted) setCommodityPresets(results);
      })
      .catch((error) => {
        // Ignore cancellations; clear suggestions on a real failure.
        if (!axios.isCancel(error)) setCommodityPresets([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsSearching(false);
      });
  }, []);

  const handleChange = useCallback(
    (nextValue: string) => {
      // Swallow the trailing onChange Mantine fires right after an option pick.
      if (didJustSubmitRef.current) {
        didJustSubmitRef.current = false;
        return;
      }

      onChange(nextValue);

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

      if (nextValue.length < MIN_SEARCH_LENGTH) {
        abortControllerRef.current?.abort();
        setIsSearching(false);
        setCommodityPresets([]);
        return;
      }

      debounceTimerRef.current = setTimeout(() => runSearch(nextValue), SEARCH_DEBOUNCE_MS);
    },
    [onChange, runSearch],
  );

  const handleOptionSubmit = useCallback(
    (itemValue: string) => {
      const commodityPreset = commodityPresetById.get(extractCommodityPresetId(itemValue));
      if (commodityPreset) {
        didJustSubmitRef.current = true;
        onChange(commodityPreset.name);
        onCommodityPresetSelect(commodityPreset);
      }
    },
    [commodityPresetById, onChange, onCommodityPresetSelect],
  );

  return (
    <Autocomplete
      data={data}
      data-testid={dataTestId}
      // The server has already fuzzy-matched, so Mantine's client-side substring
      // filter is disabled — otherwise a valid server match (e.g. "widgit" -> "Widget")
      // could be hidden client-side because the typed text isn't a literal substring.
      filter={({ options }) => options}
      label={label}
      onChange={handleChange}
      onOptionSubmit={handleOptionSubmit}
      placeholder={placeholder}
      renderOption={({ option }) => extractLabel(option.value)}
      rightSection={isSearching ? <Loader size='xs' /> : <FontAwesomeIcon icon={faMagnifyingGlass} size='sm' />}
      value={value}
    />
  );
};
